<template>
  <div>

    <!-- Logo bar -->
    <div v-if="details" class="logo-bar">
      <img
        v-if="details?.business?.logo"
        :src="url.hostName + details.business.logo"
        :alt="details?.business?.name || 'Business'"
        class="logo-img"
      />
      <div v-else class="logo-default" :style="{ background: businessAvatarColor }">
        {{ businessInitials }}
      </div>
    </div>

    <!-- Page Header -->
    <div v-if="details" class="page-header">
      <div>
        <h5 class="page-title">
          Complete Your Employee Profile
          <span v-if="details?.business?.name"> — {{ details.business.name }}</span>
        </h5>
        <p class="page-sub">Please fill in all required information to complete your onboarding.</p>
      </div>
    </div>

    <!-- Loading invite details -->
    <div v-if="loadingInvite" class="state-block">
      <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
      <p class="mt-3 text-muted-cell">Loading your invitation...</p>
    </div>

    <!-- Invalid / expired invite -->
    <div v-else-if="inviteError" class="state-block">
      <div class="state-icon state-icon--red"><font-awesome-icon icon="exclamation-triangle" /></div>
      <p class="state-title">Invalid Invitation</p>
      <p class="state-sub">{{ inviteError }}</p>
    </div>

    <!-- SUCCESS SCREEN -->
    <div v-else-if="submitted" class="success-wrapper">
      <div class="success-card">
        <div class="success-icon">
          <font-awesome-icon icon="check-circle" />
        </div>
        <h2 class="success-title">Profile Complete!</h2>
        <p class="success-msg">
          Your employee profile has been successfully submitted.
          Your account is now being set up — you will receive a confirmation email shortly.
        </p>
        <div class="success-meta" v-if="details?.business?.name">
          <span class="success-company">{{ details.business.name }}</span>
        </div>
        <div class="success-checks">
          <div class="success-check-item">
            <font-awesome-icon icon="check" class="chk" /> Personal information saved
          </div>
          <div class="success-check-item">
            <font-awesome-icon icon="check" class="chk" /> Financial details recorded
          </div>
          <div class="success-check-item">
            <font-awesome-icon icon="check" class="chk" /> Documents uploaded
          </div>
          <div class="success-check-item">
            <font-awesome-icon icon="check" class="chk" /> Account password set
          </div>
        </div>
        <p class="success-footer">You can close this window.</p>
      </div>
    </div>

    <!-- FORM -->
    <div v-else class="form-wrapper">

      <!-- STEP INDICATOR -->
      <div class="stepper-card">
        <div class="stepper">
          <template v-for="(step, i) in steps" :key="step.number">
            <div class="step">
              <div class="step-circle"
                :class="{
                  'step-circle--done':    currentStep > step.number,
                  'step-circle--active':  currentStep === step.number,
                  'step-circle--pending': currentStep < step.number,
                }">
                <font-awesome-icon v-if="currentStep > step.number" icon="check" />
                <span v-else>{{ step.number }}</span>
              </div>
              <span class="step-label"
                :class="currentStep >= step.number ? 'step-label--active' : 'step-label--pending'">
                {{ step.title }}
              </span>
            </div>
            <div v-if="i < steps.length - 1" class="step-line"
              :class="currentStep > step.number ? 'step-line--done' : 'step-line--pending'" />
          </template>
        </div>
        <div class="step-mobile-label">
          Step {{ currentStep }} of {{ steps.length }} &mdash; {{ steps[currentStep - 1].title }}
        </div>
      </div>

      <!-- FORM CARD -->
      <div class="form-card">

        <!-- Step 1: Personal Information -->
        <div v-if="currentStep === 1">
          <h2 class="step-title">Personal Information</h2>

          <div class="avatar-section">
            <div class="avatar-preview" v-if="profilePreview">
              <img :src="profilePreview" alt="Profile preview" class="avatar-img" />
              <button type="button" class="avatar-remove" @click="removePhoto">
                <font-awesome-icon icon="trash" />
              </button>
            </div>
            <div class="avatar-placeholder" v-else>
              <font-awesome-icon icon="upload" class="avatar-placeholder-icon" />
            </div>
            <div>
              <label for="profilePicInput" class="btn-upload">
                {{ profilePreview ? 'Change Photo' : 'Upload Photo' }}
              </label>
              <input id="profilePicInput" type="file" accept="image/*" style="display:none"
                @change="onPhotoChange" />
              <p class="upload-hint">JPG, PNG or GIF. Max 2 MB.</p>
            </div>
          </div>

          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">First Name <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="form.first_name"
                :class="{ 'field-input--error': errors.first_name }" placeholder="Enter first name" readonly/>
              <span v-if="errors.first_name" class="field-error">{{ errors.first_name }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Middle Name</label>
              <input type="text" class="field-input" v-model="form.middle_name" placeholder="Enter middle name" />
            </div>
            <div class="field-group">
              <label class="field-label">Last Name <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="form.last_name"
                :class="{ 'field-input--error': errors.last_name }" placeholder="Enter last name" readonly/>
              <span v-if="errors.last_name" class="field-error">{{ errors.last_name }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Official Email <span class="req">*</span></label>
              <input type="email" class="field-input" v-model="form.email"
                :class="{ 'field-input--error': errors.email }" placeholder="you@example.com" readonly required />
              <span v-if="errors.email" class="field-error">{{ errors.email }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Personal Email </label>
              <input type="email" class="field-input" v-model="form.personal_email"
                :class="{ 'field-input--error': errors.personal_email }" placeholder="you@example.com" />
              <span v-if="errors.personal_email" class="field-error">{{ errors.personal_email }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Phone <span class="req">*</span></label>
              <input type="tel" class="field-input" v-model="form.phone"
                :class="{ 'field-input--error': errors.phone }" placeholder="+234 800 000 0000" />
              <span v-if="errors.phone" class="field-error">{{ errors.phone }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Date of Birth <span class="req">*</span></label>
              <input type="date" class="field-input" v-model="form.dob"
                :class="{ 'field-input--error': errors.dob }" :max="today" />
              <span v-if="errors.dob" class="field-error">{{ errors.dob }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Gender <span class="req">*</span></label>
              <select class="field-input field-select" v-model="form.gender"
                :class="{ 'field-input--error': errors.gender }">
                <option value="">— Select gender —</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              <span v-if="errors.gender" class="field-error">{{ errors.gender }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Post Code</label>
              <input type="text" class="field-input" v-model="form.post_code" placeholder="IG8 7B" />
            </div>
            <div class="field-group field-group--full">
              <label class="field-label">Residential Address <span class="req">*</span></label>
              <textarea class="field-input" rows="3" v-model="form.address"
                :class="{ 'field-input--error': errors.address }"
                placeholder="Enter residential address"></textarea>
              <span v-if="errors.address" class="field-error">{{ errors.address }}</span>
            </div>
          </div>
        </div>


        <!-- Step 2: Financial Information -->
        <div v-if="currentStep === 2">
          <h2 class="step-title">Financial Information</h2>
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Bank Name <span class="req">*</span></label>
              <select class="field-input field-select" v-model="form.bank_id"
                :class="{ 'field-input--error': errors.bank_id }">
                <option value="">— Select bank —</option>
                <option v-for="b in filteredBanks" :key="b.id" :value="b.id">{{ b.name }}</option>
              </select>
              <span v-if="errors.bank_id" class="field-error">{{ errors.bank_id }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Account Number <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="form.account_number"
                :class="{ 'field-input--error': errors.account_number }"
                :placeholder="bankCountry === 'GB' ? '8-digit account number' : '10-digit account number'"
                :maxlength="bankCountry === 'GB' ? 8 : 10"
                inputmode="numeric"
                @input="onAccountNumberInput" />
              <span v-if="errors.account_number" class="field-error">{{ errors.account_number }}</span>
            </div>
            <div class="field-group field-group--full">
              <label class="field-label">Account Name <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="form.account_name"
                :class="{ 'field-input--error': errors.account_name }"
                placeholder="Enter account name" />
              <span v-if="errors.account_name" class="field-error">{{ errors.account_name }}</span>
            </div>
            <div v-if="bankCountry === 'GB'" class="field-group field-group--full">
              <label class="field-label">Sort Code <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="form.bank_sortcode"
                :class="{ 'field-input--error': errors.bank_sortcode }"
                placeholder="6-digit sort code e.g. 200000"
                maxlength="6"
                inputmode="numeric"
                @input="onSortCodeInput" />
              <span v-if="errors.bank_sortcode" class="field-error">{{ errors.bank_sortcode }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Pension Provider</label>
              <select class="field-input field-select" v-model="form.pension_provider_id">
                <option value="">— Select provider —</option>
                <option v-for="p in filteredPensionProviders" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Pension ID Number</label>
              <input type="text" class="field-input" v-model="form.pension_id_number"
                placeholder="Enter pension ID" />
            </div>
            <div class="field-group field-group--full">
              <label class="field-label">
                {{ bankCountry === 'GB' ? 'National Insurance Number (NI)' : 'Tax Identification Number' }}
              </label>
              <input type="text" class="field-input" v-model="form.tax_id_number"
                :class="{ 'field-input--error': errors.tax_id_number }"
                :placeholder="bankCountry === 'GB' ? 'e.g. AB123456C' : 'Enter tax identification number'"
                :maxlength="bankCountry === 'GB' ? 9 : undefined"
                @input="bankCountry === 'GB' ? onNiNumberInput($event) : null" />
              <span v-if="errors.tax_id_number" class="field-error">{{ errors.tax_id_number }}</span>
            </div>
          </div>
        </div>


        <!-- Step 3: Documents -->
        <div v-if="currentStep === 3">
          <h2 class="step-title">Documents</h2>
          <p class="step-sub">Upload supporting documents. All files are optional.</p>
          <div class="doc-add-row">
            <div class="field-group" style="flex:1;">
              <label class="field-label">Document Type</label>
              <select class="field-input field-select" v-model="newDoc.type">
                <option value="">— Select type —</option>
                <option value="national_id">National ID</option>
                <option value="passport">Passport</option>
                <option value="drivers_license">Driver's Licence</option>
                <option value="birth_certificate">Birth Certificate</option>
                <option value="degree_certificate">Degree Certificate</option>
                <option value="professional_cert">Professional Certificate</option>
                <option value="resume">CV / Resume</option>
                <option value="employment_contract">Employment Contract</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="field-group" style="flex:1;">
              <label class="field-label">Label <span class="doc-label-hint">(optional)</span></label>
              <input type="text" class="field-input" v-model="newDoc.label"
                placeholder="e.g. NIN Card, B.Sc Certificate" />
            </div>
            <div class="field-group" style="flex:1.4;">
              <label class="field-label">File</label>
              <label class="doc-file-btn" :class="{ 'doc-file-btn--chosen': newDoc.file }">
                <font-awesome-icon :icon="newDoc.file ? 'file-alt' : 'paperclip'" class="me-1" />
                {{ newDoc.file ? newDoc.file.name : 'Choose file...' }}
                <input type="file" style="display:none" @change="onDocFileChange" />
              </label>
            </div>
            <div class="field-group" style="justify-content:flex-end; padding-top:22px;">
              <button type="button" class="btn-add-doc"
                :disabled="!newDoc.type || !newDoc.file"
                @click="addDocument">
                <font-awesome-icon icon="plus" class="me-1" /> Add
              </button>
            </div>
          </div>
          <div v-if="documents.length" class="doc-list">
            <div class="doc-list-header">
              <span>Type</span><span>Label</span><span>File</span><span></span>
            </div>
            <div v-for="(doc, i) in documents" :key="i" class="doc-list-row">
              <span class="doc-type-badge">{{ docTypeLabel(doc.type) }}</span>
              <span class="text-muted-cell">{{ doc.label || '—' }}</span>
              <span class="doc-filename">
                <font-awesome-icon icon="file-alt" class="me-1" />
                {{ doc.file.name }}
                <span class="doc-size">({{ formatFileSize(doc.file.size) }})</span>
              </span>
              <button type="button" class="doc-remove-btn" @click="removeDocument(i)">
                <font-awesome-icon icon="trash" />
              </button>
            </div>
          </div>
          <div v-else class="doc-empty">
            <font-awesome-icon icon="folder-open" class="doc-empty-icon" />
            <p>No documents added yet.</p>
            <p class="doc-empty-hint">Use the form above to attach documents.</p>
          </div>
        </div>


        <!-- Step 4: Emergency Contact -->
        <div v-if="currentStep === 4">
          <h2 class="step-title">Emergency Contact</h2>
          <p class="step-sub">All fields are optional. Used in case of an emergency.</p>
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Full Name</label>
              <input type="text" class="field-input" v-model="form.emergency_name"
                placeholder="e.g. Jane Doe" />
            </div>
            <div class="field-group">
              <label class="field-label">Relationship</label>
              <select class="field-input field-select" v-model="form.emergency_relationship">
                <option value="">— Select relationship —</option>
                <option value="Spouse">Spouse</option>
                <option value="Parent">Parent</option>
                <option value="Sibling">Sibling</option>
                <option value="Child">Child</option>
                <option value="Friend">Friend</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Phone Number</label>
              <input type="tel" class="field-input" v-model="form.emergency_phone"
                placeholder="+234 800 000 0000" />
            </div>
            <div class="field-group">
              <label class="field-label">Email Address</label>
              <input type="email" class="field-input" v-model="form.emergency_email"
                placeholder="contact@example.com" />
            </div>
            <div class="field-group field-group--full">
              <label class="field-label">Address</label>
              <textarea class="field-input" rows="3" v-model="form.emergency_address"
                placeholder="Enter contact's residential address"></textarea>
            </div>
          </div>
        </div>


        <!-- Step 5: Account Password -->
        <div v-if="currentStep === 5">
          <h2 class="step-title">Account Password</h2>
          <p class="step-sub">Setup your account password</p>
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Account Password <span class="req">*</span></label>
              <input type="password" class="field-input" v-model="form.password"
                :class="{ 'field-input--error': errors.password }"
                placeholder="Enter account password" />
              <span v-if="errors.password" class="field-error">{{ errors.password }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Confirm Account Password <span class="req">*</span></label>
              <input type="password" class="field-input" v-model="form.password_confirmation"
                :class="{ 'field-input--error': errors.password_confirmation }"
                placeholder="Confirm account password" />
              <span v-if="errors.password_confirmation" class="field-error">{{ errors.password_confirmation }}</span>
            </div>
          </div>
        </div>


        <!-- Navigation -->
        <div class="form-nav">
          <button type="button" class="btn-outline"
            :disabled="currentStep === 1"
            @click="prevStep">
            <font-awesome-icon icon="arrow-left" class="me-1" /> Previous
          </button>
          <button type="button" class="btn-primary-action"
            :disabled="submitting"
            @click="nextOrSubmit">
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <font-awesome-icon v-else-if="currentStep === 5" icon="check" class="me-1" />
            {{ submitting ? 'Submitting...' : currentStep === 5 ? 'Complete Profile' : 'Next Step' }}
            <font-awesome-icon v-if="!submitting && currentStep < 5" icon="arrow-right" class="ms-1" />
          </button>
        </div>

      </div>
    </div>

  </div>
</template>


<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import hostName from '../../../../main';

export default {
  name: 'EmployeeInvitePage',

  data() {
    return {
      currentStep:   1,
      submitting:    false,
      loadingInvite: true,
      inviteError:   null,
      submitted:     false,
      url: hostName,

      details: null,

      steps: [
        { number: 1, title: 'Personal Info' },
        { number: 2, title: 'Financial' },
        { number: 3, title: 'Documents' },
        { number: 4, title: 'Emergency Contact' },
        { number: 5, title: 'Account Password' },
      ],

      profilePicture: null,
      profilePreview: null,

      form: {
        first_name:   '',
        middle_name:  '',
        last_name:    '',
        email:        '',
        personal_email: '',
        phone:        '',
        dob:          '',
        gender:       '',
        address:      '',
        post_code:    '',
        bank_id:             '',
        account_number:      '',
        account_name:        '',
        bank_sortcode:       '',
        pension_provider_id: '',
        pension_id_number:   '',
        tax_id_number:       '',
        emergency_name:         '',
        emergency_relationship: '',
        emergency_phone:        '',
        emergency_email:        '',
        emergency_address:      '',
        password:              '',
        password_confirmation:  '',
      },

      errors: {},

      newDoc:    { type: '', label: '', file: null },
      documents: [],

      banks:            [],
      pensionProviders: [],
      loadingPositions: false,

      toast: useToast(),
    };
  },

  computed: {
    today() {
      return new Date().toISOString().split('T')[0];
    },

    businessCountry() {
      return this.details?.business?.country_code?.toUpperCase() ?? null;
    },

    filteredBanks() {
      if (!this.businessCountry) return this.banks;
      return this.banks.filter(b => b.country_code?.toUpperCase() === this.businessCountry);
    },

    filteredPensionProviders() {
      if (!this.businessCountry) return this.pensionProviders;
      return this.pensionProviders.filter(p => p.country_code?.toUpperCase() === this.businessCountry);
    },

    selectedBank() {
      return this.filteredBanks.find(b => b.id == this.form.bank_id) ?? null;
    },

    bankCountry() {
      return this.selectedBank?.country_code?.toUpperCase() ?? this.businessCountry;
    },

    businessInitials() {
      const name = this.details?.business?.name || '';
      return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(w => w[0].toUpperCase())
        .join('');
    },

    businessAvatarColor() {
      const name = this.details?.business?.name || '';
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      const palette = [
        '#004AAD', '#0066cc', '#6d28d9', '#0891b2',
        '#047857', '#b45309', '#be185d', '#dc2626',
      ];
      return palette[Math.abs(hash) % palette.length];
    },
  },

  watch: {
    bankCountry(newVal) {
      this.form.account_number = '';
      this.form.tax_id_number = '';
      if (newVal !== 'GB') this.form.bank_sortcode = '';
      this.errors = { ...this.errors, account_number: undefined, bank_sortcode: undefined, tax_id_number: undefined };
    },
  },

  created() {
    this.fetchInviteAndLookups();
  },

  methods: {
    fetchInviteAndLookups() {
      this.loadingInvite = true;
      const { database, token } = this.$route.params;

      Promise.allSettled([
        axios.get(`/employees-invite/${database}/${token}`),
      ]).then(([invite]) => {

        if (invite.status === 'fulfilled') {
          this.details = invite.value.data?.data?.data ?? null;
          this.banks = invite.value.data?.data?.banks ?? [];
          this.pensionProviders = invite.value.data?.data?.pensions ?? [];

          if (this.details?.email)          this.form.email          = this.details.email;
          if (this.details?.first_name)     this.form.first_name     = this.details.first_name;
          if (this.details?.last_name)      this.form.last_name      = this.details.last_name;
          if (this.details?.personal_email) this.form.personal_email = this.details.personal_email;
        } else {
          this.inviteError = invite.reason?.response?.data?.message
            ?? 'This invitation link is invalid or has expired.';
        }
      }).finally(() => {
        this.loadingInvite = false;
      });
    },

    onAccountNumberInput(e) {
      this.form.account_number = e.target.value.replace(/\D/g, '').slice(0, this.bankCountry === 'GB' ? 8 : 10);
      e.target.value = this.form.account_number;
    },

    onSortCodeInput(e) {
      this.form.bank_sortcode = e.target.value.replace(/\D/g, '').slice(0, 6);
      e.target.value = this.form.bank_sortcode;
    },

    onNiNumberInput(e) {
      // Allow only alphanumeric, uppercase, max 9 chars
      this.form.tax_id_number = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 9);
      e.target.value = this.form.tax_id_number;
    },

    onPhotoChange(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      this.profilePicture = file;
      const reader = new FileReader();
      reader.onloadend = () => { this.profilePreview = reader.result; };
      reader.readAsDataURL(file);
    },

    removePhoto() {
      this.profilePicture = null;
      this.profilePreview = null;
    },

    onDocFileChange(e) {
      this.newDoc.file = e.target.files?.[0] ?? null;
    },

    addDocument() {
      if (!this.newDoc.type || !this.newDoc.file) return;
      this.documents.push({ ...this.newDoc });
      this.newDoc = { type: '', label: '', file: null };
      const input = this.$el.querySelector('input[type="file"][style="display:none"]:last-of-type');
      if (input) input.value = '';
    },

    removeDocument(index) {
      this.documents.splice(index, 1);
    },

    docTypeLabel(type) {
      const map = {
        national_id: 'National ID', passport: 'Passport',
        drivers_license: "Driver's Licence", birth_certificate: 'Birth Certificate',
        degree_certificate: 'Degree Certificate', professional_cert: 'Professional Cert',
        resume: 'CV / Resume', employment_contract: 'Employment Contract', other: 'Other',
      };
      return map[type] ?? type;
    },

    formatFileSize(bytes) {
      if (bytes < 1024)        return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    validateStep() {
      const errs = {};

      if (this.currentStep === 1) {
        if (!this.form.first_name.trim() || this.form.first_name.length < 2)
          errs.first_name = 'First name must be at least 2 characters.';
        if (!this.form.last_name.trim() || this.form.last_name.length < 2)
          errs.last_name = 'Last name must be at least 2 characters.';
        if (!this.form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email))
          errs.email = 'A valid email address is required.';
        if (!this.form.phone.trim() || this.form.phone.length < 10)
          errs.phone = 'A valid phone number is required.';
        if (!this.form.dob)
          errs.dob = 'Date of birth is required.';
        if (!this.form.gender)
          errs.gender = 'Gender is required.';
        if (!this.form.address.trim() || this.form.address.length < 5)
          errs.address = 'Residential address is required.';
      }

      if (this.currentStep === 2) {
        if (!this.form.bank_id)
          errs.bank_id = 'Bank is required.';

        const acct = String(this.form.account_number ?? '').replace(/\D/g, '');
        if (this.bankCountry === 'GB') {
          if (acct.length !== 8)
            errs.account_number = 'Account number must be exactly 8 digits for a UK bank.';
        } else if (this.bankCountry === 'NG') {
          if (acct.length !== 10)
            errs.account_number = 'Account number must be exactly 10 digits for a Nigerian bank.';
        } else {
          if (!acct.length)
            errs.account_number = 'Account number is required.';
        }

        if (!this.form.account_name.trim() || this.form.account_name.length < 2)
          errs.account_name = 'Account name is required.';

        if (this.bankCountry === 'GB') {
          const sc = String(this.form.bank_sortcode ?? '').replace(/\D/g, '');
          if (sc.length !== 6)
            errs.bank_sortcode = 'Sort code must be exactly 6 digits.';

          const ni = String(this.form.tax_id_number ?? '').trim().toUpperCase();
          if (ni && !/^[A-Z]{2}\d{6}[A-Z]$/.test(ni))
            errs.tax_id_number = 'National Insurance Number must be 2 letters, 6 digits, then 1 letter (e.g. AB123456C).';
        }
      }

      if (this.currentStep === 5) {
        if (!this.form.password || this.form.password.length < 8)
          errs.password = 'Password must be at least 8 characters.';
        if (this.form.password !== this.form.password_confirmation)
          errs.password_confirmation = 'Passwords do not match.';
      }

      this.errors = errs;
      return Object.keys(errs).length === 0;
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.errors = {};
        this.currentStep--;
      }
    },

    nextOrSubmit() {
      if (!this.validateStep()) return;
      if (this.currentStep < 5) {
        this.errors = {};
        this.currentStep++;
      } else {
        this.submitEmployee();
      }
    },

    async submitEmployee() {
      this.submitting = true;
      try {
        const { database, token } = this.$route.params;
        const fd = new FormData();

        fd.append('first_name',   this.form.first_name);
        fd.append('middle_name',  this.form.middle_name);
        fd.append('last_name',    this.form.last_name);
        fd.append('email',        this.form.email);
        fd.append('personal_email', this.form.personal_email);
        fd.append('phone_number', this.form.phone);
        fd.append('dob',          this.form.dob);
        fd.append('address',      this.form.address);
        fd.append('post_code',    this.form.post_code);
        fd.append('gender',       this.form.gender);
        if (this.profilePicture) fd.append('avatar', this.profilePicture);

        fd.append('department_id', this.form.department_id || '');
        fd.append('unit_id',       this.form.unit_id       || '');
        fd.append('position_id',   this.form.position_id   || '');
        fd.append('pay_grade_id',  this.form.paygrade_id   || '');
        fd.append('location_id',   this.form.location_id   || '');
        fd.append('hire_date',     this.form.hire_date      || '');
        fd.append('supervisor',    this.form.supervisor     || '');

        fd.append('all_bank_id',        String(this.form.bank_id));
        fd.append('bank_account_no',    this.form.account_number);
        fd.append('bank_account_name',  this.form.account_name);
        fd.append('bank_sortcode',      this.form.bank_sortcode      || '');
        fd.append('pension_company_id', String(this.form.pension_provider_id || ''));
        fd.append('pension_number',     this.form.pension_id_number  || '');
        fd.append('tax_number',         this.form.tax_id_number      || '');

        fd.append('emergency_name',         this.form.emergency_name         || '');
        fd.append('emergency_relationship', this.form.emergency_relationship || '');
        fd.append('emergency_phone',        this.form.emergency_phone        || '');
        fd.append('emergency_email',        this.form.emergency_email        || '');
        fd.append('emergency_address',      this.form.emergency_address      || '');

        fd.append('password',              this.form.password);
        fd.append('password_confirmation', this.form.password_confirmation);

        this.documents.forEach((doc, i) => {
          fd.append(`documents[${i}][type]`,  doc.type);
          fd.append(`documents[${i}][label]`, doc.label || '');
          fd.append(`documents[${i}][file]`,  doc.file, doc.file.name);
        });

        fd.append('token',    token);
        fd.append('database', database);

        const { data } = await axios.post(`/employees-invite/${database}/${token}`, fd);

        this.toast.success(data?.message || 'Profile completed successfully.');
        this.submitted = true;
        window.scrollTo({ top: 0, behavior: 'smooth' });

      } catch (err) {
        const msg = this.flatError(err.response?.data)
          || err.message
          || 'Failed to submit. Please try again.';
        this.toast.error(msg);
      } finally {
        this.submitting = false;
      }
    },

    flatError(data) {
      if (!data || typeof data !== 'object') return null;
      const msg = data.message;
      if (typeof msg === 'string') return msg;
      if (Array.isArray(msg)) return msg[0];
      if (typeof msg === 'object') return Object.values(msg).flat()[0] ?? null;
      const errs = data.data;
      if (errs && typeof errs === 'object') return Object.values(errs).flat()[0] ?? null;
      return null;
    },
  },
};
</script>


<style scoped>
/* ── Utilities ── */
.me-1 { margin-right:4px; }
.me-2 { margin-right:8px; }
.ms-1 { margin-left:4px; }
.mt-3 { margin-top:12px; }
.text-muted-cell { color:#4a5e78; font-size:13px; }

/* ── Logo & Page Header ── */
.logo-bar    { display:flex; align-items:center; justify-content:center; padding:28px 16px 0; margin-bottom:8px; }
.logo-img    { height:52px; width:auto; object-fit:contain; max-width:100%; }
.logo-default { height:52px; width:52px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:20px; font-weight:800; color:#fff; letter-spacing:0.5px; flex-shrink:0; }
.page-header { margin-bottom:24px; text-align:center; padding:0 16px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

/* ── Form Wrapper ── */
.form-wrapper { max-width:900px; margin:0 auto; padding:0 16px 32px; display:flex; flex-direction:column; gap:20px; box-sizing:border-box; }

/* ── Stepper ── */
.stepper-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:24px 28px; }
.stepper      { display:flex; align-items:center; justify-content:center; }
.step         { display:flex; flex-direction:column; align-items:center; gap:8px; min-width:0; }
.step-circle  { width:44px; height:44px; border-radius:50%; border:2px solid; display:flex; align-items:center; justify-content:center; font-size:15px; font-weight:700; transition:all .2s; flex-shrink:0; }
.step-circle--done    { background:#004AAD; border-color:#004AAD; color:#fff; }
.step-circle--active  { background:#fff; border-color:#004AAD; color:#004AAD; }
.step-circle--pending { background:#fff; border-color:#dde5f0; color:#94a3b8; }
.step-label           { font-size:12px; font-weight:600; white-space:nowrap; }
.step-label--active   { color:#1a2b45; }
.step-label--pending  { color:#94a3b8; }
.step-line            { width:48px; height:2px; margin:0 6px; margin-bottom:22px; flex-shrink:0; transition:background .2s; }
.step-line--done      { background:#004AAD; }
.step-line--pending   { background:#e4eaf2; }
.step-mobile-label    { display:none; }

/* ── Form Card ── */
.form-card  { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:36px 40px; }
.step-title { font-size:22px; font-weight:800; color:#1a2b45; margin:0 0 6px; }
.step-sub   { font-size:13.5px; color:#7a90b0; margin:0 0 24px; }

/* ── Avatar ── */
.avatar-section     { display:flex; align-items:center; gap:20px; margin-bottom:28px; flex-wrap:wrap; }
.avatar-preview     { position:relative; flex-shrink:0; }
.avatar-img         { width:80px; height:80px; border-radius:50%; object-fit:cover; border:3px solid #e4eaf2; }
.avatar-remove      { position:absolute; top:-6px; right:-6px; width:26px; height:26px; border-radius:50%; background:#e03535; color:#fff; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:11px; }
.avatar-placeholder { width:80px; height:80px; border-radius:50%; border:2px dashed #dde5f0; background:#f8fafc; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.avatar-placeholder-icon { font-size:24px; color:#c0c9d8; }
.btn-upload  { display:inline-block; padding:8px 16px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; font-weight:600; color:#1a2b45; background:#fff; cursor:pointer; }
.btn-upload:hover { background:#f4f7fb; }
.upload-hint { font-size:11.5px; color:#7a90b0; margin:6px 0 0; }

/* ── Fields ── */
.fields-grid       { display:grid; grid-template-columns:1fr 1fr; gap:18px; }
.field-group       { display:flex; flex-direction:column; gap:5px; }
.field-group--full { grid-column:1/-1; }
.field-label       { font-size:13px; font-weight:600; color:#1a2b45; }
.req               { color:#e03535; }
.field-input       { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:9px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; box-sizing:border-box; }
.field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.field-input--error { border-color:#e03535; }
.field-input:disabled { background:#f4f7fb; color:#94a3b8; cursor:not-allowed; }
.field-input[readonly] { background:#f4f7fb; color:#64748b; }
.field-select      { appearance:auto; cursor:pointer; }
textarea.field-input { resize:vertical; }
.field-error       { font-size:12px; color:#e03535; }

/* ── Documents ── */
.doc-add-row { display:grid; grid-template-columns:1fr 1fr 1.4fr auto; gap:14px; align-items:flex-end; padding:20px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; margin-bottom:20px; }
.doc-label-hint { font-size:11px; color:#94a3b8; font-weight:400; margin-left:4px; }
.doc-file-btn { display:inline-flex; align-items:center; gap:6px; padding:9px 12px; border:1.5px dashed #dde5f0; border-radius:9px; font-size:13px; color:#7a90b0; background:#fff; cursor:pointer; transition:all .15s; width:100%; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; box-sizing:border-box; }
.doc-file-btn:hover, .doc-file-btn--chosen { border-color:#004AAD; color:#004AAD; }
.btn-add-doc { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:9px; border:none; cursor:pointer; font-family:inherit; white-space:nowrap; }
.btn-add-doc:hover:not(:disabled) { background:#003b8e; }
.btn-add-doc:disabled { opacity:.45; cursor:not-allowed; }
.doc-list        { border:1px solid #e4eaf2; border-radius:10px; overflow:hidden; }
.doc-list-header { display:grid; grid-template-columns:160px 1fr 1fr 40px; gap:12px; padding:10px 16px; background:#f8fafc; border-bottom:1px solid #e4eaf2; font-size:11px; font-weight:700; text-transform:uppercase; color:#7a90b0; }
.doc-list-row    { display:grid; grid-template-columns:160px 1fr 1fr 40px; gap:12px; padding:12px 16px; align-items:center; border-bottom:1px solid #f0f4f8; }
.doc-list-row:last-child { border-bottom:none; }
.doc-type-badge  { background:rgba(0,74,173,.08); color:#004AAD; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; white-space:nowrap; }
.doc-filename    { font-size:13px; color:#1a2b45; font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.doc-size        { font-size:11.5px; color:#94a3b8; }
.doc-remove-btn  { width:28px; height:28px; border-radius:7px; border:1px solid #fecaca; background:#fff0f0; color:#e03535; display:flex; align-items:center; justify-content:center; font-size:11px; cursor:pointer; flex-shrink:0; }
.doc-empty       { display:flex; flex-direction:column; align-items:center; padding:48px 24px; text-align:center; border:1px dashed #e4eaf2; border-radius:10px; }
.doc-empty-icon  { font-size:32px; color:#c0c9d8; margin-bottom:12px; }
.doc-empty p     { font-size:14px; font-weight:600; color:#7a90b0; margin:0 0 4px; }
.doc-empty-hint  { font-size:12.5px; color:#94a3b8; }

/* ── Nav buttons ── */
.form-nav    { display:flex; justify-content:space-between; align-items:center; padding-top:28px; margin-top:28px; border-top:1px solid #e4eaf2; gap:12px; }
.btn-primary-action { display:inline-flex; align-items:center; justify-content:center; gap:7px; background:#004AAD; color:#fff; font-size:13.5px; font-weight:600; padding:11px 28px; border-radius:9px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.5; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; justify-content:center; gap:7px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13.5px; font-weight:600; padding:11px 24px; border-radius:9px; cursor:pointer; font-family:inherit; }
.btn-outline:hover:not(:disabled) { background:#f4f7fb; }
.btn-outline:disabled { opacity:.45; cursor:not-allowed; }

/* ── State / Error screens ── */
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:80px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-icon--red { background:rgba(224,53,53,.1); color:#e03535; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }

/* ── Success screen ── */
.success-wrapper { max-width:560px; margin:40px auto; padding:0 16px 32px; box-sizing:border-box; }
.success-card    { background:#fff; border:1px solid #e4eaf2; border-radius:16px; padding:48px 40px; text-align:center; }
.success-icon    { font-size:60px; color:#16a34a; margin-bottom:20px; line-height:1; }
.success-title   { font-size:26px; font-weight:800; color:#1a2b45; margin:0 0 12px; }
.success-msg     { font-size:14px; color:#7a90b0; margin:0 auto 24px; line-height:1.7; max-width:400px; }
.success-meta    { margin-bottom:24px; }
.success-company { display:inline-block; background:#f0f4fb; color:#004AAD; font-size:13px; font-weight:600; padding:5px 16px; border-radius:20px; border:1px solid #dde5f0; }
.success-checks  { display:flex; flex-direction:column; gap:10px; text-align:left; background:#f8fafc; border-radius:10px; padding:20px 24px; margin:0 0 28px; }
.success-check-item { display:flex; align-items:center; gap:10px; font-size:13.5px; color:#1a2b45; font-weight:500; }
.chk             { color:#16a34a; font-size:13px; flex-shrink:0; }
.success-footer  { font-size:12.5px; color:#94a3b8; margin:0; }

/* ── Tablet (≤768px) ── */
@media (max-width:768px) {
  .form-card       { padding:28px 24px; }
  .stepper-card    { padding:20px 16px; }
  .step-line       { width:32px; }
  .fields-grid     { grid-template-columns:1fr 1fr; gap:14px; }
  .doc-add-row     { grid-template-columns:1fr 1fr; }
}

/* ── Mobile (≤600px) ── */
@media (max-width:600px) {
  .page-title      { font-size:17px; }
  .form-wrapper    { padding:0 12px 24px; gap:14px; }
  .stepper-card    { padding:16px 12px; }

  /* Hide step labels, show compact dot strip */
  .step-label      { display:none; }
  .step-circle     { width:34px; height:34px; font-size:13px; }
  .step-line       { width:20px; margin:0 4px; margin-bottom:0; }

  /* Show the "Step X of 5 — Title" line instead */
  .step-mobile-label {
    display:block;
    text-align:center;
    margin-top:14px;
    font-size:13px;
    font-weight:600;
    color:#1a2b45;
  }

  .form-card       { padding:20px 16px; border-radius:12px; }
  .step-title      { font-size:18px; }

  /* Single column fields */
  .fields-grid     { grid-template-columns:1fr; gap:14px; }
  .field-group--full { grid-column:1; }

  /* Avatar centered */
  .avatar-section  { flex-direction:column; align-items:center; text-align:center; gap:14px; }

  /* Documents add row: stack */
  .doc-add-row     { grid-template-columns:1fr; gap:12px; padding:16px; }
  .btn-add-doc     { width:100%; justify-content:center; }

  /* Document list: card style */
  .doc-list-header { display:none; }
  .doc-list-row    { grid-template-columns:1fr auto; grid-template-rows:auto auto; gap:6px 8px; padding:12px 14px; }
  .doc-list-row .doc-type-badge { grid-column:1; }
  .doc-list-row .doc-filename   { grid-column:1; font-size:12px; }
  .doc-list-row .text-muted-cell { grid-column:1; }
  .doc-list-row .doc-remove-btn { grid-column:2; grid-row:1 / span 2; align-self:center; }

  /* Nav buttons full width, stacked */
  .form-nav        { flex-direction:column-reverse; gap:10px; }
  .btn-primary-action,
  .btn-outline     { width:100%; font-size:14px; padding:13px; }

  /* Success */
  .success-wrapper { margin:20px auto; padding:0 12px 24px; }
  .success-card    { padding:28px 18px; border-radius:12px; }
  .success-title   { font-size:20px; }
  .success-icon    { font-size:48px; }
  .success-checks  { padding:16px; }
  .success-check-item { font-size:13px; }

  /* State screen */
  .state-block     { padding:48px 20px; }
}

/* ── Small phones (≤400px) ── */
@media (max-width:400px) {
  .form-wrapper    { padding:0 8px 20px; }
  .form-card       { padding:16px 12px; }
  .step-circle     { width:30px; height:30px; font-size:12px; }
  .step-line       { width:12px; margin:0 2px; }
  .field-input     { font-size:13px; padding:8px 10px; }
  .btn-primary-action,
  .btn-outline     { font-size:13px; padding:12px; }
}
</style>